<template>
  <div>
    <div
      class="
        tw-mt-20 tw-mb-6
        login-form
        tw-border-solid
        tw-border
        tw-border-purellGray
        tw-p-4
        tw-bg-white
        tw-rounded
        tw-w-full
        tw-max-w-xl
        tw-m-auto
      "
    >
      <login-logo />

      <div class="tw-mb-6">
        <div
          v-if="errorMessage"
          class="tw-text-md tw-font-bold tw-mb-0 tw-text-red-500"
        >
          {{ errorMessage }}
        </div>
      </div>

      <ValidationObserver ref="form" v-slot="{ failed }">
        <v-form @submit.prevent="signIn" class="tw-mb-6">
          <ValidationProvider
            :name="$t('log-in.form.email-address')"
            :rules="{ required: true, email: true }"
            v-slot="{ errors }"
          >
            <v-text-field
              v-model="email"
              :error-messages="errors"
              name="email"
              id="email"
              :label="$t('log-in.form.email-address')"
              :placeholder="autofilled ? ' ' : null"
              :persistent-placeholder="autofilled"
              @focus="autofilled = false"
            />
          </ValidationProvider>

          <ValidationProvider
            :name="$t('log-in.form.password')"
            :rules="{ required: true }"
            v-slot="{ errors }"
          >
            <v-text-field
              v-model="password"
              :error-messages="errors"
              name="password"
              id="password"
              :label="$t('log-in.form.password')"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPassword ? 'text' : 'password'"
              @click:append="showPassword = !showPassword"
              :placeholder="autofilled ? ' ' : null"
              :persistent-placeholder="autofilled"
              @focus="autofilled = false"
            />
          </ValidationProvider>

          <v-checkbox
            :label="`${$t('log-in.form.remember-me')}`"
            v-model="rememberMe"
          />

          <v-btn
            :disabled="failed || loadingPassword"
            :loading="loadingPassword"
            color="primary"
            block
            type="submit"
          >
            {{ $t("log-in.form.log-in") }}
          </v-btn>
        </v-form>
      </ValidationObserver>

      <button
        type="button"
        class="ms-signin-btn"
        :disabled="loadingPassword"
        @click="signInWithMicrosoft"
      >
        <span class="ms-signin-inner">
          <img class="ms-signin-logo" :src="MSLogo" alt="" aria-hidden="true" />

          <span class="ms-signin-text">
            {{ $t("log-in.form.log-in-with-microsoft") }}
          </span>
        </span>
      </button>

      <a
        href="/login/forgot"
        class="tw-text-blue-500 tw-no-underline hover:tw-underline"
      >
        {{ $t("log-in.forgot-your-password") }}?
      </a>
    </div>

    <div class="tw-flex tw-justify-center">
      <country-dropdown />
    </div>

    <v-dialog v-model="showModal" max-width="460" persistent>
      <v-card class="tw-py-4">
        <v-card-text style="color: red">
          {{
            $t(pastMarchFirst ? "ie11-popup.warning2" : "ie11-popup.warning")
          }}
        </v-card-text>
        <v-card-text style="color: black">
          {{ $t("ie11-popup.better-experience") }}
        </v-card-text>
        <v-card-text style="color: black">
          {{ $t("ie11-popup.recommended-browsers") }}
        </v-card-text>

        <div
          class="tw-flex tw-flex-row tw-items-center tw-cursor-pointer tw-px-6"
        >
          <div
            class="tw-flex tw-flex-col tw-items-center"
            @click="openBrowser('Edge')"
          >
            <v-img :src="MSEdgeLogo" class="browserLogo" />
            <p>{{ $t("ie11-popup.edge") }}</p>
          </div>

          <div
            class="
              tw-flex tw-flex-col tw-items-center tw-cursor-pointer tw-ml-8
            "
            @click="openBrowser('Chrome')"
          >
            <v-img :src="ChromeLogo" class="browserLogo" />
            <p>{{ $t("ie11-popup.chrome") }}</p>
          </div>
        </div>

        <v-btn
          icon
          absolute
          style="top: 0; right: 0"
          @click="showModal = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import LoginLogo from "@/views/login/components/Logo.vue";
import CountryDropdown from "@/components/ui/CountryDropdown.vue";
import { ROUTE_NAMES } from "@/types/routeNames";
import { mapGetters } from "vuex";
import i18n from "@/plugins/i18n";
import ChromeLogo from "@/assets/images/logos/chromeLogo.svg";
import MSEdgeLogo from "@/assets/images/logos/msEdgeLogo.svg";
import MSLogo from "@/assets/images/logos/ms-symbollockup_mssymbol_19.svg";

const SSO_ERROR_TO_I18N_KEY = {
  CANCELED: "log-in.sso.canceled",
  GENERIC: "log-in.sso.generic"
};

const ALLOWED_UI_SSO_ERROR_CODES = new Set(["CANCELED", "GENERIC"]);

export default {
  components: {
    LoginLogo,
    CountryDropdown
  },
  data: () => ({
    autofilled: false,
    email: "",
    intervalDetectAutofill: null,
    localStorageKey: "smartLinkLogInEmail",
    loadingPassword: false,
    password: "",
    rememberMe: false,
    errorMessage: "",
    pastMarchFirst: new Date() > new Date("March 1 2023 00:00:00"),
    showModal: false,
    showPassword: false,
    ChromeLogo,
    MSEdgeLogo,
    MSLogo
  }),
  computed: {
    ...mapGetters("application", ["isChromiumBrowser"])
  },
  mounted() {
    this.applyQueryErrors();

    const savedLoginEmail = localStorage.getItem(this.localStorageKey);
    if (savedLoginEmail) {
      this.email = savedLoginEmail;
      this.rememberMe = true;
    }

    if (this.isChromiumBrowser) {
      this.intervalDetectAutofill = setInterval(() => {
        if (document.querySelector('input[type="password"]:-webkit-autofill')) {
          this.autofilled = true;
        }
      }, 100);

      setTimeout(() => {
        if (this.intervalDetectAutofill) {
          clearInterval(this.intervalDetectAutofill);
          this.intervalDetectAutofill = null;
        }
      }, 3000);
    }

    // IE11 modal
    this.showModal = window.document.documentMode;
  },
  beforeDestroy() {
    if (this.intervalDetectAutofill) {
      clearInterval(this.intervalDetectAutofill);
      this.intervalDetectAutofill = null;
    }
  },
  methods: {
    applyQueryErrors() {
      const query = this.$route?.query || {};
      const ssoError = query.ssoError;
      const timeout = query.timeout;

      if (ssoError) {
        const code = String(ssoError).trim().toUpperCase();
        const i18nKey = ALLOWED_UI_SSO_ERROR_CODES.has(code)
          ? SSO_ERROR_TO_I18N_KEY[code]
          : SSO_ERROR_TO_I18N_KEY.GENERIC;
        this.errorMessage = i18n.t(i18nKey);
        return;
      }

      if (timeout) {
        this.errorMessage = i18n.t("log-in.user-timeout");
      }
    },

    async signIn() {
      this.loadingPassword = true;
      this.errorMessage = "";

      const valid = await this.$refs.form.validate();
      if (!valid) {
        this.loadingPassword = false;
        return;
      }

      if (this.rememberMe) {
        localStorage.setItem(this.localStorageKey, this.email);
      } else {
        localStorage.removeItem(this.localStorageKey);
      }

      try {
        const response = await fetch("/api/Login", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            UserName: this.email,
            Password: this.password,
            RememberMe: this.rememberMe
          })
        });

        if (response.ok) {
          this.handleSuccess();
          return;
        }

        this.errorMessage = i18n.t("log-in.invalid-credentials");
      } catch (e) {
        this.errorMessage = i18n.t("log-in.invalid-credentials");
      } finally {
        this.loadingPassword = false;
      }
    },

    signInWithMicrosoft() {
      window.location = "/api/signin-entra-start";
    },

    handleSuccess() {
      const returnUrl =
        this.$route.query?.ReturnUrl ?? this.$route.query?.returnUrl;

      const queryParams = { ...this.$route.query };
      delete queryParams.returnUrl;
      delete queryParams.ReturnUrl;
      delete queryParams.timeout;
      delete queryParams.ssoError;

      if (returnUrl) {
        this.$router
          .push({ path: decodeURIComponent(returnUrl), query: queryParams })
          .catch(() => {
            /* ignore double redirect */
          });
      } else {
        this.$router.push({ name: ROUTE_NAMES.redirectionRoute }).catch(() => {
          /* ignore double redirect */
        });
      }
    },

    openBrowser(type) {
      if (type === "Chrome") {
        window.open("https://www.google.com/chrome/");
      } else {
        window.open(
          "https://www.microsoft.com/en-us/edge/download?form=MA13FJ"
        );
      }
    }
  },
  watch: {
    autofilled() {
      if (this.intervalDetectAutofill) {
        clearInterval(this.intervalDetectAutofill);
        this.intervalDetectAutofill = null;
      }
    },
    "$route.query": {
      handler() {
        this.applyQueryErrors();
      },
      deep: true
    }
  }
};
</script>

<style lang="postcss" scoped>
.browserLogo {
  height: 60px;
  width: 60px;
}

.ms-signin-btn {
  margin-bottom: 1rem;
  width: 100%;
  height: 36px;

  background: #ffffff;
  border: 1px solid #c8c8c8;
  border-radius: 4px;

  cursor: pointer;

  transition: background-color 120ms ease, border-color 120ms ease,
    box-shadow 120ms ease;
}

.ms-signin-btn:hover:not(:disabled) {
  background: #f5f5f5;
}

.ms-signin-btn:focus {
  outline: none;
}

@supports selector(:focus-visible) {
  .ms-signin-btn:focus:not(:focus-visible) {
    border-color: #c8c8c8;
    box-shadow: none;
  }
}

.ms-signin-btn:focus-visible {
  outline: none;
  border-color: #007bb6;
  box-shadow: 0 0 0 3px rgba(0, 123, 182, 0.25);
}

.ms-signin-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ms-signin-inner {
  height: 100%;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 10px;
  padding: 0 14px;
}

.ms-signin-logo {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}

.ms-signin-text {
  color: #5f5f5f;
  font-weight: 600;
  line-height: 1;
}

.ms-signin-spinner {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;

  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
